// src/pages/admin/technicians/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import { useLabPermissions } from "../../../hooks/useLabPermissions";

// Badges sin clases dinámicas (evita purge)
const BADGE_MAP = {
  slate: "bg-slate-800/20 text-slate-300 border border-slate-700/40",
  emerald: "bg-emerald-800/20 text-emerald-300 border border-emerald-700/40",
  sky: "bg-sky-800/20 text-sky-300 border border-sky-700/40",
  amber: "bg-amber-800/20 text-amber-300 border border-amber-700/40",
  blue: "bg-blue-800/20 text-blue-300 border border-blue-700/40",
};
const Badge = ({ children, color = "slate" }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${BADGE_MAP[color] || BADGE_MAP.slate}`}>
    {children}
  </span>
);

async function getFunctionErrorMessage(error) {
  try {
    const payload = await error?.context?.json?.();
    if (payload?.error) return payload.error;
  } catch {
    // La respuesta puede no tener cuerpo JSON; usar el mensaje estándar.
  }
  return error?.message || "No se pudo crear la cuenta del técnico.";
}

// Modal de creación/edición (en el mismo archivo para copy-paste simple)
function TechnicianModal({ open, onClose, onSaved, initial, canCreateAccount }) {
  const isEdit = !!initial?.id;
  const defaultAccountMode = canCreateAccount
    ? (isEdit ? (initial?.user_id ? "none" : "link") : "password")
    : "none";
  const [saving, setSaving] = useState(false);

  const [fullName, setFullName] = useState(initial?.full_name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  // UI usa coma/enter para skills, en BD se guarda text[]
  const initialSkills = Array.isArray(initial?.skills) ? initial.skills.join(", ") : (initial?.skills || "");
  const [skillsText, setSkillsText] = useState(initialSkills);
  const [active, setActive] = useState(initial?.active ?? true);
  const [accountMode, setAccountMode] = useState(defaultAccountMode);
  const [initialPassword, setInitialPassword] = useState("");
  const [initialPasswordAgain, setInitialPasswordAgain] = useState("");

  useEffect(() => {
    if (open) {
      setFullName(initial?.full_name || "");
      setEmail(initial?.email || "");
      setPhone(initial?.phone || "");
      setSkillsText(Array.isArray(initial?.skills) ? initial.skills.join(", ") : (initial?.skills || ""));
      setActive(initial?.active ?? true);
      setAccountMode(
        canCreateAccount
          ? (initial?.id ? (initial?.user_id ? "none" : "link") : "password")
          : "none"
      );
      setInitialPassword("");
      setInitialPasswordAgain("");
    }
  }, [open, initial, canCreateAccount]);

  const normalizeSkills = (txt) => {
    if (!txt) return null; // guarda null si está vacío
    const arr = String(txt)
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    return arr.length ? arr : null;
  };

  const nullIfEmpty = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      alert("El nombre es obligatorio.");
      return;
    }
    if (accountMode !== "none" && !initial?.user_id && !email.trim()) {
      alert("El email es obligatorio para crear o invitar la cuenta.");
      return;
    }
    if (accountMode === "password" && !initial?.user_id) {
      if (initialPassword.length < 8) {
        alert("La contraseña inicial debe tener al menos 8 caracteres.");
        return;
      }
      if (initialPassword !== initialPasswordAgain) {
        alert("Las contraseñas no coinciden.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        full_name: fullName.trim(),
        email: nullIfEmpty(email),
        phone: nullIfEmpty(phone),
        skills: normalizeSkills(skillsText), // text[] o null
        active: !!active,
      };

      // ⬇️ ASEGURAR SESIÓN (CLAVE PARA RLS)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error("La sesión no está lista. Intente nuevamente.");
      }

      let successMessage = "";
      const shouldConfigureAccount = accountMode !== "none" && !initial?.user_id;

      if (isEdit && !shouldConfigureAccount) {
        const { error } = await supabase
          .from("technicians")
          .update(payload)
          .eq("id", initial.id);

        if (error) throw error;
      } else if (!isEdit && accountMode === "none") {
        const { error } = await supabase
          .from("technicians")
          .insert(payload);

        if (error) throw error;
        successMessage = "Ficha de técnico creada sin cuenta de acceso.";
      } else {
        const { data, error } = await supabase.functions.invoke(
          "admin-technician-account",
          {
            body: {
              mode: accountMode,
              technician_id: initial?.id || undefined,
              ...payload,
              password: accountMode === "password" ? initialPassword : undefined,
            },
          }
        );
        if (error) throw new Error(await getFunctionErrorMessage(error));
        if (!data?.ok) throw new Error(data?.error || "No se pudo completar el alta.");
        successMessage = data.message || "Cuenta y técnico creados correctamente.";
      }

      onSaved?.();
      onClose?.();
      if (successMessage) window.alert(successMessage);
    } catch (err) {
      console.error(err);
      alert(err.message || "No se pudo guardar el técnico.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-card text-card-foreground shadow-2xl">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? "Editar técnico" : "Nuevo técnico"}</h3>
          <button onClick={onClose} className="rounded px-2 py-1 hover:bg-muted/40" aria-label="Cerrar">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm mb-1">Nombre completo *</label>
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Ej: Juan Pérez"
            />
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Email</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                type="email"
              />
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className="block text-sm mb-1">Teléfono</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+54 9 261 ..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Skills (separadas por coma)</label>
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              value={skillsText}
              onChange={(e) => setSkillsText(e.target.value)}
              placeholder="pressure, temperature, dimensional"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Se guardan como <code>text[]</code>. Dejar vacío si no aplica.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input id="active" type="checkbox" className="h-4 w-4" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <label htmlFor="active" className="text-sm">Activo</label>
          </div>

          {canCreateAccount && !initial?.user_id && (
            <fieldset className="space-y-3 rounded-xl border border-sky-500/20 bg-sky-500/[0.06] p-4">
              <legend className="px-1 text-sm font-semibold text-sky-200">Cuenta de acceso</legend>

              {isEdit && (
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    name="account-mode"
                    value="link"
                    checked={accountMode === "link"}
                    onChange={(event) => setAccountMode(event.target.value)}
                    className="mt-1 accent-sky-500"
                  />
                  <span>
                    <span className="block text-sm font-medium">Vincular cuenta existente</span>
                    <span className="block text-xs text-slate-400">Busca una cuenta con el mismo email y conserva los roles administrador o jefe de laboratorio.</span>
                  </span>
                </label>
              )}

              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="account-mode"
                  value="password"
                  checked={accountMode === "password"}
                  onChange={(event) => setAccountMode(event.target.value)}
                  className="mt-1 accent-sky-500"
                />
                <span>
                  <span className="block text-sm font-medium">Crear con contraseña inicial</span>
                  <span className="block text-xs text-slate-400">Puede ingresar inmediatamente; el administrador entrega la contraseña.</span>
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="account-mode"
                  value="invite"
                  checked={accountMode === "invite"}
                  onChange={(event) => setAccountMode(event.target.value)}
                  className="mt-1 accent-sky-500"
                />
                <span>
                  <span className="block text-sm font-medium">Enviar invitación por correo</span>
                  <span className="block text-xs text-slate-400">El técnico recibe un enlace y crea su propia contraseña.</span>
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="account-mode"
                  value="none"
                  checked={accountMode === "none"}
                  onChange={(event) => setAccountMode(event.target.value)}
                  className="mt-1 accent-sky-500"
                />
                <span>
                  <span className="block text-sm font-medium">{isEdit ? "Guardar sin vincular cuenta" : "Solo ficha, sin acceso"}</span>
                  <span className="block text-xs text-slate-400">{isEdit ? "Actualiza los datos y mantiene la ficha sin acceso." : "Útil para personal que no utilizará la aplicación."}</span>
                </span>
              </label>

              {accountMode === "password" && (
                <div className="grid grid-cols-1 gap-3 border-t border-white/10 pt-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs text-slate-300">Contraseña inicial</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={initialPassword}
                      onChange={(event) => setInitialPassword(event.target.value)}
                      className="w-full rounded-md border bg-background px-3 py-2"
                      placeholder="Mínimo 8 caracteres"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-300">Repetir contraseña</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={initialPasswordAgain}
                      onChange={(event) => setInitialPasswordAgain(event.target.value)}
                      className="w-full rounded-md border bg-background px-3 py-2"
                      placeholder="Repetir contraseña"
                    />
                  </div>
                </div>
              )}

              {accountMode === "invite" && (
                <p className="border-t border-white/10 pt-3 text-xs leading-5 text-amber-200/90">
                  El envío depende del servicio de correo configurado en Supabase. Si se alcanza el límite de emails, el alta se cancela sin dejar una ficha incompleta.
                </p>
              )}
            </fieldset>
          )}

          {!canCreateAccount && !initial?.user_id && (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm leading-6 text-amber-100">
              Solo un administrador puede crear o invitar cuentas de acceso. Con tu rol se guardará únicamente la ficha operativa del técnico.
            </div>
          )}

          {isEdit && initial?.user_id && (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              Esta ficha ya tiene una cuenta vinculada. Guardar cambios no modifica su contraseña ni su rol.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded border border-slate-600/60 px-3 py-1 text-slate-300 hover:bg-slate-600/10">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded border border-blue-600/60 px-3 py-1 text-blue-300 hover:bg-blue-600/10"
            >
              {saving
                ? "Guardando…"
                : (isEdit && accountMode !== "none" && !initial?.user_id)
                  ? "Crear / vincular cuenta"
                  : (isEdit ? "Guardar cambios" : "Crear técnico")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const FORCE_RELOAD_KEY = "global_force_reload_lab";

function ForceReloadButton() {
  const [status, setStatus] = React.useState("idle"); // idle | sending | done

  const handleClick = async () => {
    if (status === "sending") return;
    const ok = window.confirm("¿Forzar recarga en todas las computadoras conectadas?");
    if (!ok) return;
    setStatus("sending");
    try {
      await triggerForceReload();
      setStatus("done");
      setTimeout(() => setStatus("idle"), 4000);
    } catch (e) {
      console.error(e);
      setStatus("idle");
      alert("No se pudo enviar la señal de recarga.");
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={status !== "idle"}
      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors shadow ${
        status === "done"
          ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
          : status === "sending"
          ? "border-amber-500/40 bg-amber-500/10 text-amber-300 cursor-wait"
          : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
      }`}
      title="Fuerza que todas las computadoras conectadas recarguen la app"
    >
      {status === "sending" ? "Enviando…" : status === "done" ? "✓ Recarga enviada" : "↺ Recargar todo"}
    </button>
  );
}

async function triggerForceReload() {
  const rpc = (enabled) =>
    supabase.rpc("trigger_app_flag_reload", { p_enabled: enabled });

  // Paso 1: asegurar false (Postgres no dispara evento si el valor no cambia)
  const r1 = await rpc(false);
  console.log("[ForceReload] rpc false →", r1.error);
  if (r1.error) throw r1.error;

  // Paso 2: breve pausa
  await new Promise((r) => setTimeout(r, 600));

  // Paso 3: poner true — dispara el evento en todos los clientes
  const r2 = await rpc(true);
  console.log("[ForceReload] rpc true →", r2.error);
  if (r2.error) throw r2.error;

  // Paso 4: volver a false después de 4s
  setTimeout(async () => {
    const r3 = await rpc(false);
    console.log("[ForceReload] reset false →", r3.error);
  }, 4000);
}

export default function TechniciansAdminPage() {
  const { isAdmin } = useLabPermissions();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // filtros
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "", "true", "false"
  const [skillFilter, setSkillFilter] = useState("");   // texto contiene

  // datos
  const [rows, setRows] = useState([]);
  const [workloadByTech, setWorkloadByTech] = useState({}); // { techId: {open_count, in_progress_count, done_count} }

  // modal
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      // 1) Técnicos base
      let query = supabase
        .from("technicians")
        .select("id, full_name, email, phone, skills, active, created_at, user_id")
        .order("full_name", { ascending: true });

      if (activeFilter === "true") query = query.eq("active", true);
      if (activeFilter === "false") query = query.eq("active", false);

      const { data, error } = await query;
      if (error) throw error;

      // 2) Workload (opcional): si existe la vista, la usamos
      let wl = {};
      const { data: wdata, error: werr } = await supabase
        .from("v_technician_workload")
        .select("technician_id, open_count, in_progress_count, done_count");
      if (!werr && Array.isArray(wdata)) {
        wl = (wdata || []).reduce((acc, r) => {
          acc[r.technician_id] = {
            open_count: Number(r.open_count || 0),
            in_progress_count: Number(r.in_progress_count || 0),
            done_count: Number(r.done_count || 0),
          };
          return acc;
        }, {});
      }
      setWorkloadByTech(wl);

      // 3) Búsqueda local + filtro de skills
      let list = data || [];
      if (q) {
        const needle = q.trim().toLowerCase();
        list = list.filter(r =>
          (r.full_name || "").toLowerCase().includes(needle) ||
          (r.email || "").toLowerCase().includes(needle) ||
          (r.phone || "").toLowerCase().includes(needle)
        );
      }
      if (skillFilter.trim()) {
        const sNeedle = skillFilter.trim().toLowerCase();
        list = list.filter(r => {
          const skills = Array.isArray(r.skills) ? r.skills : String(r.skills || "").split(",").map(s => s.trim());
          return skills.some(s => s.toLowerCase().includes(sNeedle));
        });
      }

      setRows(list);
    } catch (e) {
      console.error(e);
      setErr(e.message || "No se pudieron cargar los técnicos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refiltrar al cambiar filtros (server-side para activo; los demás locales)
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const items = useMemo(() => rows || [], [rows]);

  const openNew = () => { setEditing(null); setOpenModal(true); };
  const openEdit = (row) => { setEditing(row); setOpenModal(true); };

  const toggleActive = async (row) => {
    try {
      const { error } = await supabase.from("technicians").update({ active: !row.active }).eq("id", row.id);
      if (error) throw error;
      await load();
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudo cambiar el estado.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pb-16">
      <header className="flex items-center justify-between gap-3 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Técnicos</h1>
          <p className="text-sm text-muted-foreground">Directorio de técnicos y su carga de trabajo.</p>
        </div>
        <div className="flex items-center gap-2">
          <ForceReloadButton />
          <button
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow"
            onClick={openNew}
          >
            + Nuevo técnico
          </button>
        </div>
      </header>

      {/* Filtros */}
      <div className="rounded-xl border border-border bg-card p-4 mb-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-5">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
              placeholder="Buscar por nombre / email / teléfono…"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
            >
              <option value="">Estado: todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
          <div className="col-span-12 md:col-span-4">
            <input
              value={skillFilter}
              onChange={(e) => setSkillFilter(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
              placeholder="Filtrar por skill (ej: pressure)…"
            />
          </div>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded border border-red-900/40 bg-red-900/10 p-3 text-sm text-red-300">
          {err}
        </div>
      )}

      {/* Tabla */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">Listado</h2>
          <Badge color="sky">{items.length}</Badge>
        </div>

        <div className="overflow-x-auto rounded border border-slate-800/60">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/40 text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">Técnico</th>
                <th className="px-3 py-2 text-left">Contacto</th>
                <th className="px-3 py-2 text-left">Skills</th>
                <th className="px-3 py-2 text-left">OS abiertas</th>
                <th className="px-3 py-2 text-left">En progreso</th>
                <th className="px-3 py-2 text-left">Terminadas</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td className="px-3 py-4 text-slate-400" colSpan={8}>Cargando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td className="px-3 py-4 text-slate-400" colSpan={8}>No hay técnicos.</td></tr>
              )}

              {items.map((t) => {
                const wl = workloadByTech[t.id] || { open_count: 0, in_progress_count: 0, done_count: 0 };
                const skillsArr = Array.isArray(t.skills)
                  ? t.skills
                  : String(t.skills || "").split(",").map(s => s.trim()).filter(Boolean);

                return (
                  <tr key={t.id} className="border-t border-slate-800/60 hover:bg-slate-900/30">
                    <td className="px-3 py-2 font-medium">
                      <div>{t.full_name || "—"}</div>
                      <div className="mt-1">
                        <Badge color={t.user_id ? "emerald" : "slate"}>
                          {t.user_id ? "Cuenta vinculada" : "Solo ficha"}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-200">{t.email || "—"}</div>
                      <div className="text-slate-400 text-xs">{t.phone || "—"}</div>
                    </td>
                    <td className="px-3 py-2">
                      {skillsArr.length ? (
                        <div className="flex flex-wrap gap-1">
                          {skillsArr.map((s, i) => (
                            <Badge key={i} color="amber">{s}</Badge>
                          ))}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">{wl.open_count ?? 0}</td>
                    <td className="px-3 py-2">{wl.in_progress_count ?? 0}</td>
                    <td className="px-3 py-2">{wl.done_count ?? 0}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge color={t.active ? "emerald" : "slate"}>{t.active ? "Activo" : "Inactivo"}</Badge>
                    </td>
                    <td className="px-3 py-2 text-center space-x-2">
                      <button
                        className="rounded border border-slate-600/60 px-3 py-1 text-slate-300 hover:bg-slate-600/10"
                        onClick={() => openEdit(t)}
                      >
                        Editar
                      </button>
                      {isAdmin && !t.user_id && (
                        <button
                          className="rounded border border-sky-600/60 px-3 py-1 text-sky-300 hover:bg-sky-600/10"
                          onClick={() => openEdit(t)}
                          title={t.email ? "Crear o vincular cuenta de acceso" : "Primero agregá un email"}
                        >
                          {t.email ? "Crear cuenta" : "Agregar email"}
                        </button>
                      )}
                      <button
                        className={`rounded px-3 py-1 ${t.active
                          ? "border border-rose-600/60 text-rose-300 hover:bg-rose-600/10"
                          : "border border-emerald-600/60 text-emerald-300 hover:bg-emerald-600/10"
                          }`}
                        onClick={() => toggleActive(t)}
                        title={t.active ? "Desactivar" : "Activar"}
                      >
                        {t.active ? "Desactivar" : "Activar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Modal */}
      <TechnicianModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSaved={load}
        initial={editing}
        canCreateAccount={isAdmin}
      />
    </div>
  );
}
